"use client";

import React from "react";
import { useTranslations } from "next-intl";
import styled from "styled-components";
import type { EmeaEntity } from "@/lib/constants/emeaEntities";
import { applyEntity, LegalPart } from "./helpers";

type Translator = ReturnType<typeof useTranslations>;

// Turns a <tag>text</tag> segment into a node (e.g. a clickable doc link).
export type DocLinkRenderer = (tag: string, text: string) => React.ReactNode;

const Body = styled.div`
  display: flex;
  flex-direction: column;

  h2 {
    color: #212121;
    font-size: 1.25rem;
    line-height: 1.35;
    margin: 0;
  }

  .subtitle {
    color: #000000;
    font-size: 1rem;
    font-weight: 600;
    margin: 8px 0 0;
  }

  h3 {
    color: #003865;
    font-size: 1.125rem;
    line-height: 1.4;
    margin: 32px 0 0;
    padding-bottom: 9px;
    border-bottom: 1px solid #e0e6ee;
  }

  p {
    color: #000000;
    font-size: 0.9375rem;
    line-height: 1.75;
    margin: 16px 0 0;
  }

  ul {
    color: #000000;
    font-size: 0.9375rem;
    line-height: 1.75;
    margin: 12px 0 0;
    padding-left: 24px;

    li {
      margin-top: 8px;
    }
  }

  .doc-link {
    color: ${(props) => props.theme.link};
    cursor: pointer;
    text-decoration: underline;
  }

  @media (max-width: 1024px) {
    h2 {
      font-size: 1.125rem;
    }

    h3 {
      font-size: 1rem;
    }

    p,
    ul {
      font-size: 0.875rem;
    }
  }
`;

const DOC_TAG = /<(\w+)>([\s\S]*?)<\/\1>/g;

// Renders text, turning <tag>…</tag> segments into doc links when a renderer is
// provided (enrollment), or plain text (tags stripped) otherwise (legal page).
function renderText(
  text: string,
  renderDocLink?: DocLinkRenderer
): React.ReactNode {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  DOC_TAG.lastIndex = 0;
  while ((match = DOC_TAG.exec(text)) !== null) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    const [full, tag, inner] = match;
    nodes.push(
      renderDocLink ? (
        <React.Fragment key={match.index}>
          {renderDocLink(tag, inner)}
        </React.Fragment>
      ) : (
        inner
      )
    );
    last = match.index + full.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes.length <= 1 ? nodes[0] ?? "" : nodes;
}

function renderPart(
  part: LegalPart,
  index: number,
  t: Translator,
  subst: (text: string) => string,
  renderDocLink?: DocLinkRenderer
) {
  // Read with t.raw so next-intl doesn't ICU-parse the pre-formatted legal copy
  // (our {{entityName}} placeholders and stray apostrophes are not valid ICU).
  switch (part.kind) {
    case "subtitle":
      return (
        <p className="subtitle" key={index}>
          {subst(t.raw(part.key) as string)}
        </p>
      );
    case "heading":
      return <h3 key={index}>{subst(t.raw(part.key) as string)}</h3>;
    case "paragraph":
      return (
        <p key={index}>
          {renderText(subst(t.raw(part.key) as string), renderDocLink)}
        </p>
      );
    case "list": {
      const items = (t.raw(part.key) as string[]) ?? [];
      return (
        <ul key={index}>
          {items.map((item, i) => (
            <li key={i}>{subst(item)}</li>
          ))}
        </ul>
      );
    }
  }
}

// Renders a legal document: a title (h2) followed by an ordered list of parts
// (subtitle / section heading / paragraph / bulleted list). `entity` fills the
// EMEA {{entityName}} / {{entityAddress}} placeholders. `renderDocLink` makes
// tagged document names clickable (used in the enrollment flow).
export function LegalDocumentBody({
  t,
  titleKey,
  parts,
  entity = null,
  renderDocLink,
}: {
  t: Translator;
  titleKey: string;
  parts: LegalPart[];
  entity?: EmeaEntity | null;
  renderDocLink?: DocLinkRenderer;
}) {
  const subst = (text: string) => applyEntity(text, entity);

  return (
    <Body>
      <h2>{subst(t.raw(titleKey) as string)}</h2>
      {parts.map((part, index) =>
        renderPart(part, index, t, subst, renderDocLink)
      )}
    </Body>
  );
}
